import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import List from '@mui/material/List';
import Collapse from '@mui/material/Collapse';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { listNewFeatures } from './NewFeaturesListData';
import { monthlyReleaseVideos } from './MonthlyReleaseVideoList';
import "styles/sass/support/topics/NewFeature.scss";

const ListExampleNested = () => {
  const [groupedByMonth, setGroupedByMonth] = useState({});
  const [detailPage, setDetailPage] = useState(listNewFeatures[0]);
  const [videoPage, setVideoPage] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    // Group features by month when component mounts
    setGroupedByMonth(groupFeaturesByMonth(listNewFeatures));
  }, []);

  const groupFeaturesByMonth = (features) => {
    const today = new Date();
    const daysToDisplayNewIcon = today.setDate(today.getDate() - 31); // Change this to desired number of days to have the new icon displayed
    return features.reduce((acc, feature) => {
      const featureDate = new Date(feature.dateAdded);
      const monthYear = featureDate.toLocaleString('default', { month: 'long', year: 'numeric' });
      feature.isNew = featureDate >= daysToDisplayNewIcon;

      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }

      acc[monthYear].push(feature);
      return acc;
    }, {});
  };

  const toggleMonthExpansion = (monthYear) => {
    setExpandedMonths((prevExpanded) => ({
      ...prevExpanded,
      [monthYear]: !prevExpanded[monthYear],
    }));
  };

  const clickedFeaturePages = (feature) => {
    setDetailPage(feature);
    setVideoPage(null);
  };

  const renderNewFeatureChip = (currentDate) => {
    if (currentDate) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
          <NewReleasesIcon style={{ fill: '#37ad6f', marginRight: '5px' }} />
        </div>
      );
    }
    return null;
  };

  const convertDateFormat = (inputDate) => {
    if (!inputDate) return '';
    let dateParts = inputDate.split('/');
    let dateObject = new Date(+dateParts[2], dateParts[0] - 1, +dateParts[1]);
    let monthName = dateObject.toLocaleString('default', { month: 'long' });
    let year = dateObject.getFullYear();
    return `${monthName} ${dateParts[1]}, ${year}`;
  };

  const generateMonthlyVideoJSX = (month) => {
    const video = monthlyReleaseVideos[month];
    if (!video) {
      return <div>Video for {month} not found.</div>;
    }
    const youtubeVideoURL = convertToEmbedUrl(video.videoURL);
    return (
      <div key={month} className="title-container">
        <h1 className="header-title">{`${month} Monthly Release Video`}</h1>
        <iframe
          width="1300"
          height="800"
          src={youtubeVideoURL}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  };

  const convertToEmbedUrl = (url) => {
    if (url.includes('watch?v=')) {
      return url.replace('watch?v=', 'embed/');
    } else if (url.includes('youtu.be/')) {
      return url.replace('youtu.be/', 'www.youtube.com/embed/');
    } else {
      return url;
    }
  };

  const clickedMonthlyVideos = (month) => {
    const videoJSX = generateMonthlyVideoJSX(month);
    setVideoPage(videoJSX);
    setDetailPage(null);
  };

  return (
    <section className="New-Feature-Page">
      <aside className="New-Feature-Sidebar-Container">
        <section className="New-Feature-Container" style={{ width: '100%' }}>
          <h1 className="sidebar-title">New Features</h1>
          <List className="list">
            {Object.entries(groupedByMonth).map(([monthYear, features]) => (
              <React.Fragment key={monthYear}>
                <ListItemButton
                  component="div"
                  onClick={() => toggleMonthExpansion(monthYear)}
                >
                  <ListItemText>
                    <div className="feature-row">
                      <span style={{ paddingTop: '5px' }}>{monthYear}</span>
                      {monthlyReleaseVideos[monthYear] && (
                        <section style={{ display: 'flex' }} onClick={() => clickedMonthlyVideos(monthYear)}>
                          <span className="feature-video">
                            Feature Video
                          </span>
                          <div className="square-outline">
                            <PlayArrowIcon style={{ fill: '#7A60E5' }} />
                          </div>
                        </section>
                      )}
                    </div>
                  </ListItemText>
                  {expandedMonths[monthYear] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={expandedMonths[monthYear]} timeout="auto" unmountOnExit>
                  {features.map((feature) => (
                    <button
                      className="newFeatureTitle"
                      key={`${monthYear}-${feature.dateAdded}-${feature.title}`}
                      onClick={() => clickedFeaturePages(feature)}
                    >
                      <div className="feature-row">
                        {feature.isNew && (
                          <div className="feature-actions">{renderNewFeatureChip(feature.isNew)}</div>
                        )}
                        <div className="feature-content">
                          <h4 className="feature-title">{feature.title}</h4>
                        </div>
                      </div>
                    </button>
                  ))}
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </section>
      </aside>
      <div className="New-Feature-Detail-Page-Container">
        {detailPage && (
          <div className="title-container">
            <h1 className="header-title">{detailPage.title}</h1>
            <span className="header-date">{`Release Date:  ${convertDateFormat(detailPage.dateAdded)}`}</span>
          </div>
        )}
        <div className="Feature-Detail-container">
          {detailPage && detailPage.contents}
          {videoPage && videoPage}
        </div>
      </div>
    </section>
  );
};

export default ListExampleNested;